import React, { useState, useEffect } from 'react'
import { makeStyles } from '@material-ui/core/styles'
import { useTheme } from '@material-ui/core/styles'
import Box from '@material-ui/core/Box'
import Container from '@material-ui/core/Container'
import ReactBSAlert from 'react-bootstrap-sweetalert'
import FormGroup from '@material-ui/core/FormGroup'
import FormControl from '@material-ui/core/FormControl'
import Select from '@material-ui/core/Select'
import MenuItem from '@material-ui/core/MenuItem'
import Grid from '@material-ui/core/Grid'
import Card from '@material-ui/core/Card'
import Tooltip from '@material-ui/core/Tooltip'
import CardHeader from '@material-ui/core/CardHeader'
import Typography from '@material-ui/core/Typography'
import Tabs from '@material-ui/core/Tabs'
import Tab from '@material-ui/core/Tab'
import Loader from 'components/general/Loader'
import AdminAPI from 'config/api/AdminAPI'
import { onError } from 'config/lib/errorLib'
import FSDeliveriesTable from 'components/admin/FS/FSDeliveriesTable'
import FSBalanceTab from 'components/admin/FS/FSBalanceTab'
import { getMoments, getProductString } from 'config/helpers/Helpers'
import { productOptions } from 'config/constants/Constants'
import PeriodSelect from 'components/general/PeriodSelect'
import CardStats from 'components/argon/Cards/Dashboard/CardStats.js'
import PeopleIcon from '@material-ui/icons/People'
import CheckIcon from '@material-ui/icons/Check'
import CloseIcon from '@material-ui/icons/Close'
import ReportProblemIcon from '@material-ui/icons/ReportProblem'
import TrendingUpIcon from '@material-ui/icons/TrendingUp'
import Chip from '@material-ui/core/Chip'
import moment from 'moment-timezone'

import componentStyles from 'assets/theme/views/admin/alternative-dashboard.js'
import componentStylesCardDeck from 'assets/theme/components/cards/card-deck.js'

const useStyles = makeStyles(componentStyles)
const useStylesCardDeck = makeStyles(componentStylesCardDeck)

function FSDeliveries() {
    const classes = { ...useStyles(), ...useStylesCardDeck() }
    const theme = useTheme()
    const api = new AdminAPI()

    const [tab, setTab] = useState(0)
    const [showStatus, setShowStatus] = useState('active')
    const [allDeliveries, setAllDeliveries] = useState([])
    const [deliveries, setDeliveries] = useState([])
    const [showDeliveries, setShowDeliveries] = useState([])
    const [leadsSummary, setLeadsSummary] = useState({ deliveryCounts: {}, validCount: 0, rejectedCount: 0, unallocatedByProduct: {} })
    const [period, setPeriod] = useState('thisWeek')
    const [fromDate, setFromDate] = useState('')
    const [toDate, setToDate] = useState('')
    const [product, setProduct] = useState('all')
    const [companies, setCompanies] = useState([])
    const [loading, setLoading] = useState(true)
    const [alert, setAlert] = useState(false)

    useEffect(() => {
        loadData()
    }, [])

    async function loadData() {
        setLoading(true)
        try {
            const [fsDeliveries, companiesData] = await Promise.all([
                api.getFSDeliveries(),
                api.getCompanies()
            ])
            console.log('fs deliveries: ', fsDeliveries)

            setCompanies(companiesData)

            let dates = getMoments('thisWeek')
            let [summary, spResult] = await Promise.all([
                api.getFSLeadsPeriod(dates.fromDate.valueOf(), dates.toDate.valueOf()),
                api.getStartPensionLeadsPeriod(dates.fromDate.valueOf(), dates.toDate.valueOf())
            ])

            const enriched = enrichDeliveries(fsDeliveries, summary, spResult)

            setAllDeliveries(enriched)
            setDeliveries(enriched)
            setShowDeliveries(enriched.filter(d => d.active))
            setLeadsSummary(summary)
            setFromDate(dates.fromDate)
            setToDate(dates.toDate)
            setLoading(false)
        } catch (e) {
            onError(e)
        }
    }

    function enrichDeliveries(deliveries, summary, spCounts) {
        let enriched = deliveries.map(d => ({ ...d, leadCount: 0, revenue: 0, hvCount: 0 }))

        const counts = summary.deliveryCounts || {}
        const hvCounts = summary.highValueCounts || {}
        const bandCounts = summary.pensionBandCounts || {}
        for (let i = 0; i < enriched.length; i++) {
            const key = `${enriched[i].companyId}|${enriched[i].product}`
            const count = counts[key] || 0
            const hvCount = hvCounts[key] || 0
            enriched[i].leadCount = count
            enriched[i].hvCount = hvCount

            // Variable-priced pensions delivery: revenue per pension-value band
            if (enriched[i].product === 'pensions' && enriched[i].usePricingTable && enriched[i].pricing && bandCounts[key]) {
                let revenue = 0
                for (const [band, bandCount] of Object.entries(bandCounts[key])) {
                    const price = enriched[i].pricing[band] != null ? enriched[i].pricing[band] : enriched[i].pricing['General Enquiry (no value)']
                    revenue += bandCount * (parseFloat(price) || 0)
                }
                enriched[i].revenue = revenue
            }
            // JC Mortgages pensions: tiered pricing (HV at €250, rest at standard leadPrice)
            // — legacy fallback until JC is migrated onto variable pricing
            else if (enriched[i].companyId === 'aa9fcd90' && enriched[i].product === 'pensions' && hvCount > 0) {
                enriched[i].revenue = (hvCount * 250) + ((count - hvCount) * (enriched[i].leadPrice || 0))
            } else {
                enriched[i].revenue = count * (enriched[i].leadPrice || 0)
            }
        }

        // Override AL Start Pensions with verified count
        if (spCounts) {
            let alIdx = enriched.findIndex(d => d.isStartPensionAL)
            if (alIdx > -1 && spCounts.alCount != null) {
                enriched[alIdx].leadCount = spCounts.alCount
                enriched[alIdx].revenue = spCounts.alCount * (enriched[alIdx].leadPrice || 0)
            }

            // Override AA Start Pensions with total NPH count
            let aaIdx = enriched.findIndex(d => d.isStartPensionAA)
            if (aaIdx > -1 && spCounts.aaCount != null) {
                enriched[aaIdx].leadCount = spCounts.aaCount
                enriched[aaIdx].revenue = spCounts.aaCount * (enriched[aaIdx].leadPrice || 0)
            }
        }

        return enriched
    }

    async function updatePeriod(newPeriod, fromDateCustom, toDateCustom) {
        setPeriod(newPeriod)
        setLoading(true)
        let dates = newPeriod === 'custom' ? {
            fromDate: moment(fromDateCustom).startOf('day'),
            toDate: moment(toDateCustom).endOf('day')
        } : getMoments(newPeriod)

        try {
            const requestedProduct = product === 'all' ? null : product
            let [summary, spResult] = await Promise.all([
                api.getFSLeadsPeriod(dates.fromDate.valueOf(), dates.toDate.valueOf(), requestedProduct),
                api.getStartPensionLeadsPeriod(dates.fromDate.valueOf(), dates.toDate.valueOf())
            ])

            let baseDeliveries = product === 'all'
                ? allDeliveries.map(d => ({ ...d }))
                : allDeliveries.filter(d => d.product === product).map(d => ({ ...d }))

            const enriched = enrichDeliveries(baseDeliveries, summary, spResult)
            let checkStatus = showStatus === 'active'
            let filtered = enriched.filter(d => d.active === checkStatus)

            setDeliveries(enriched)
            setShowDeliveries(filtered)
            setLeadsSummary(summary)
            setFromDate(dates.fromDate)
            setToDate(dates.toDate)
            setLoading(false)
        } catch (e) {
            onError(e)
        }
    }

    async function updateProduct(newProduct) {
        setLoading(true)
        let dates = getMoments(period)
        try {
            const requestedProduct = newProduct === 'all' ? null : newProduct
            let [summary, spResult] = await Promise.all([
                api.getFSLeadsPeriod(dates.fromDate.valueOf(), dates.toDate.valueOf(), requestedProduct),
                api.getStartPensionLeadsPeriod(dates.fromDate.valueOf(), dates.toDate.valueOf())
            ])

            let baseDeliveries = newProduct === 'all'
                ? allDeliveries.map(d => ({ ...d }))
                : allDeliveries.filter(d => d.product === newProduct).map(d => ({ ...d }))

            const enriched = enrichDeliveries(baseDeliveries, summary, spResult)
            let checkStatus = showStatus === 'active'
            let filtered = enriched.filter(d => d.active === checkStatus)

            setDeliveries(enriched)
            setShowDeliveries(filtered)
            setLeadsSummary(summary)
            setFromDate(dates.fromDate)
            setToDate(dates.toDate)
            setProduct(newProduct)
            setLoading(false)
        } catch (e) {
            onError(e)
        }
    }

    function updateDelivery(delivery) {
        const index = deliveries.findIndex(d => d.companyId === delivery.companyId && d.product === delivery.product)
        let newDeliveries = [...deliveries]
        if (index > -1) {
            // API returns the raw bd-deliveries item; keep the row's computed fields
            // (deliveryType, leadCount, revenue, etc.) so it isn't dropped from the table.
            newDeliveries[index] = { ...newDeliveries[index], ...delivery }
        }
        let checkStatus = showStatus === 'active'
        let filtered = newDeliveries.filter(d => d.active === checkStatus)
        setDeliveries(newDeliveries)
        setShowDeliveries(filtered)
    }

    function updateShowStatus(newStatus) {
        let checkStatus = newStatus === 'active'
        let filtered = deliveries.filter(d => d.active === checkStatus)
        setShowDeliveries(filtered)
        setShowStatus(newStatus)
    }

    function showSuccessAlert(text) {
        setAlert(
            <ReactBSAlert
                success
                style={{ display: 'block', marginTop: '-100px' }}
                title='Success'
                onConfirm={() => setAlert(false)}
                onCancel={() => setAlert(false)}
                confirmBtnBsStyle='success'
                confirmBtnText='Ok'
                btnSize=''
                confirmBtnStyle={{
                    marginRight: undefined,
                    borderColor: undefined,
                    boxShadow: undefined,
                }}
            >
                {text}
            </ReactBSAlert>
        )
    }

    // Stats calculations
    const validCount = leadsSummary.validCount || 0
    const rejectedCount = leadsSummary.rejectedCount || 0
    const totalRevenue = deliveries.filter(d => d.deliveryType !== 'balance').reduce((sum, d) => sum + (parseFloat(d.revenue) || 0), 0)
    const avgLeadValue = validCount > 0 ? (totalRevenue / validCount) : 0
    const unallocatedByProduct = leadsSummary.unallocatedByProduct || {}
    const unallocatedCount = Object.values(unallocatedByProduct).reduce((sum, c) => sum + c, 0)

    const sortedUnallocatedProducts = Object.entries(unallocatedByProduct)
        .sort((a, b) => b[1] - a[1])

    // Expected revenue: sum of allocation * leadPrice for active weekly deliveries (excl. extra allocation — generally replacements)
    const expectedRevenue = period === 'thisWeek'
        ? deliveries
            .filter(d => d.deliveryType === 'weekly' && d.active)
            .reduce((sum, d) => {
                return sum + ((parseInt(d.allocation) || 0) * (parseFloat(d.leadPrice) || 0))
            }, 0)
        : 0

    // Product summary: aggregate leads & revenue per product from deliveries (excl. balance)
    const productSummary = {}
    for (let d of deliveries.filter(d => d.deliveryType !== 'balance')) {
        const prod = d.product
        if (!productSummary[prod]) {
            productSummary[prod] = { leads: 0, revenue: 0 }
        }
        productSummary[prod].leads += (d.leadCount || 0)
        productSummary[prod].revenue += (parseFloat(d.revenue) || 0)
    }
    const sortedProductSummary = Object.entries(productSummary)
        .filter(([, v]) => v.leads > 0 || v.revenue > 0)
        .sort((a, b) => b[1].revenue - a[1].revenue)

    return (
        <>
            {alert}
            <Container
                maxWidth={false}
                component={Box}
                marginTop='2rem'
                classes={{ root: classes.containerRoot }}
            >
                {/* Tabs */}
                <Tabs
                    value={tab}
                    onChange={(e, v) => setTab(v)}
                    indicatorColor='primary'
                    textColor='primary'
                    style={{ marginBottom: '1.5rem' }}
                >
                    <Tab label='Manage Deliveries' />
                    <Tab label='Manage Balances' />
                </Tabs>

                {tab === 0 && (
                    <>
                        {!loading ? (
                            <Grid container>
                                <Grid item xs={12}>
                                    <Grid
                                        container
                                        component={Box}
                                        alignItems='center'
                                        justifyContent='flex-start'
                                        style={{ marginBottom: '2rem' }}
                                        direction='row'
                                    >
                                        <Grid item xs={12} md={2}>
                                            <PeriodSelect
                                                period={period}
                                                updatePeriod={(p, from, to) => updatePeriod(p, from, to)}
                                                fromDate={fromDate}
                                                toDate={toDate}
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={2}>
                                            <FormGroup style={{ marginBottom: '0' }}>
                                                <FormControl variant='outlined'>
                                                    <Select
                                                        multiple={false}
                                                        value={product}
                                                        onChange={(e) => updateProduct(e.target.value)}
                                                    >
                                                        <MenuItem value={'all'}>All Products</MenuItem>
                                                        {productOptions.map((item) => (
                                                            <MenuItem key={item.value} value={item.value}>{item.label}</MenuItem>
                                                        ))}
                                                    </Select>
                                                </FormControl>
                                            </FormGroup>
                                            <Box
                                                fontSize='12px'
                                                color={theme.palette.gray[600]}
                                                marginTop='0.5rem'
                                                textAlign='left'
                                            >
                                                <Box component='small' fontSize='100%'>
                                                    Product
                                                </Box>
                                            </Box>
                                        </Grid>
                                        <Grid item xs={12} md={2}>
                                            <FormGroup style={{ marginBottom: '0' }}>
                                                <FormControl variant='outlined' fullWidth>
                                                    <Select
                                                        multiple={false}
                                                        value={showStatus}
                                                        onChange={(e) => updateShowStatus(e.target.value)}
                                                    >
                                                        <MenuItem value='active'>Active</MenuItem>
                                                        <MenuItem value='inactive'>Inactive</MenuItem>
                                                    </Select>
                                                </FormControl>
                                            </FormGroup>
                                            <Box
                                                fontSize='12px'
                                                color={theme.palette.gray[600]}
                                                marginTop='0.5rem'
                                                textAlign='left'
                                            >
                                                <Box component='small' fontSize='100%'>
                                                    Status
                                                </Box>
                                            </Box>
                                        </Grid>
                                    </Grid>
                                </Grid>

                                {/* Stats Cards */}
                                <Grid item xs={12}>
                                    <Grid container direction='row' spacing={1} style={{ marginBottom: '2rem', display: 'flex', flexWrap: 'wrap' }}>
                                        <Grid item xs={12} sm={6} style={{ flex: '1 1 0', minWidth: 180 }}>
                                            <Tooltip
                                                title='Excluding balance deliveries and extra allocations (replacements)'
                                                arrow
                                                placement='top'
                                            >
                                                <div>
                                                    <CardStats
                                                        subtitle='Revenue'
                                                        title={'€' + totalRevenue.toFixed(2)}
                                                        icon={CheckIcon}
                                                        color='bgSuccess'
                                                        footer={expectedRevenue > 0 ? (
                                                            <Box component='span' fontSize='12px' color='#8898aa'>
                                                                Out of <strong>€{expectedRevenue.toLocaleString('en-IE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong> expected
                                                            </Box>
                                                        ) : null}
                                                    />
                                                </div>
                                            </Tooltip>
                                        </Grid>
                                        <Grid item xs={12} sm={6} style={{ flex: '1 1 0', minWidth: 180 }}>
                                            <CardStats
                                                subtitle='Valid Leads'
                                                title={String(validCount)}
                                                icon={PeopleIcon}
                                                color='bgPrimary'
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={6} style={{ flex: '1 1 0', minWidth: 180 }}>
                                            <CardStats
                                                subtitle='Avg Lead Value'
                                                title={'€' + avgLeadValue.toFixed(2)}
                                                icon={TrendingUpIcon}
                                                color='bgInfo'
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={6} style={{ flex: '1 1 0', minWidth: 180 }}>
                                            <CardStats
                                                subtitle='Unallocated'
                                                title={String(unallocatedCount)}
                                                icon={ReportProblemIcon}
                                                color={unallocatedCount > 0 ? 'bgWarning' : 'bgInfo'}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={6} style={{ flex: '1 1 0', minWidth: 180 }}>
                                            <CardStats
                                                subtitle='Rejected'
                                                title={String(rejectedCount)}
                                                icon={CloseIcon}
                                                color='bgError'
                                            />
                                        </Grid>
                                    </Grid>
                                </Grid>

                                {/* Unallocated Breakdown */}
                                {unallocatedCount > 0 && (
                                    <Grid item xs={12}>
                                        <Box
                                            style={{
                                                backgroundColor: '#fff',
                                                borderRadius: '6px',
                                                border: '1px solid #5e72e4',
                                                padding: '1rem 1.5rem',
                                                marginBottom: '1.5rem'
                                            }}
                                        >
                                            <Box
                                                style={{
                                                    fontWeight: 600,
                                                    fontSize: '14px',
                                                    color: '#525f7f',
                                                    marginBottom: '0.75rem'
                                                }}
                                            >
                                                ⚠️ Unallocated Leads — {unallocatedCount} lead{unallocatedCount !== 1 ? 's' : ''} with no buyer
                                            </Box>
                                            <Box display='flex' flexWrap='wrap' style={{ gap: '8px' }}>
                                                {sortedUnallocatedProducts.map(([prod, count]) => (
                                                    <Chip
                                                        key={prod}
                                                        label={`${getProductString(prod)} (${count})`}
                                                        style={{
                                                            backgroundColor: '#5e72e4',
                                                            color: '#fff',
                                                            fontWeight: 600,
                                                            fontSize: '13px'
                                                        }}
                                                    />
                                                ))}
                                            </Box>
                                        </Box>
                                    </Grid>
                                )}

                                {/* Weekly Deliveries Table */}
                                <Grid item xs={12}>
                                    <FSDeliveriesTable
                                        tableType='weekly'
                                        deliveries={(() => {
                                            let items = showDeliveries
                                                .filter(d => d.deliveryType === 'weekly')
                                                .map(item => ({
                                                    ...item,
                                                    compositeKey: `${item.companyId}-${item.product}-${item.deliveryType}`
                                                }))
                                            // Calculate total allocation per company for sorting
                                            const companyTotals = {}
                                            items.forEach(d => {
                                                const cap = (parseInt(d.allocation) || 0) + (parseInt(d.extraAllocation) || 0)
                                                companyTotals[d.companyId] = (companyTotals[d.companyId] || 0) + cap
                                            })
                                            items.sort((a, b) => {
                                                const diff = companyTotals[b.companyId] - companyTotals[a.companyId]
                                                if (diff !== 0) return diff
                                                // Within same company, sort by individual allocation
                                                const capA = (parseInt(a.allocation) || 0) + (parseInt(a.extraAllocation) || 0)
                                                const capB = (parseInt(b.allocation) || 0) + (parseInt(b.extraAllocation) || 0)
                                                return capB - capA
                                            })
                                            return items
                                        })()}
                                        updateDelivery={(delivery) => updateDelivery(delivery)}
                                        showSuccessAlert={(text) => showSuccessAlert(text)}
                                        refreshDeliveries={() => loadData()}
                                        companies={companies}
                                        period={period}
                                        title='Weekly Deliveries'
                                    />
                                </Grid>

                                {/* Unlimited / Hard-coded Deliveries Table */}
                                {showDeliveries.filter(d => d.deliveryType === 'hidden').length > 0 && (
                                    <Grid item xs={12} style={{ marginTop: '2rem' }}>
                                        <FSDeliveriesTable
                                            tableType='unlimited'
                                            deliveries={(() => {
                                                let items = showDeliveries
                                                    .filter(d => d.deliveryType === 'hidden')
                                                    .map(item => ({
                                                        ...item,
                                                        compositeKey: `${item.companyId}-${item.product}-${item.deliveryType}`
                                                    }))
                                                const companyTotals = {}
                                                items.forEach(d => {
                                                    companyTotals[d.companyId] = (companyTotals[d.companyId] || 0) + (d.leadCount || 0)
                                                })
                                                items.sort((a, b) => {
                                                    const diff = companyTotals[b.companyId] - companyTotals[a.companyId]
                                                    if (diff !== 0) return diff
                                                    return (b.leadCount || 0) - (a.leadCount || 0)
                                                })
                                                return items
                                            })()}
                                            updateDelivery={(delivery) => updateDelivery(delivery)}
                                            showSuccessAlert={(text) => showSuccessAlert(text)}
                                            title='Unlimited Deliveries'
                                            footerNote='If a weekly delivery has priority enabled, it takes precedence over these until its weekly cap is reached.'
                                        />
                                    </Grid>
                                )}

                                {/* Product Summary */}
                                {product === 'all' && totalRevenue > 0 && sortedProductSummary.length > 0 && (
                                    <Grid item xs={12} style={{ marginTop: '2rem' }}>
                                        <Card>
                                            <CardHeader
                                                subheader={
                                                    <Box
                                                        component={Typography}
                                                        variant='h2'
                                                        marginBottom='0!important'
                                                    >
                                                        <Box component='span'>Revenue by Product</Box>
                                                    </Box>
                                                }
                                            />
                                            <Box style={{ padding: '0 1.5rem 1.5rem' }}>
                                                <Grid container spacing={2}>
                                                    {sortedProductSummary.filter(([, data]) => data.revenue > 0).map(([prod, data]) => {
                                                        const pct = totalRevenue > 0 ? (data.revenue / totalRevenue * 100) : 0
                                                        return (
                                                            <Grid item xs={6} sm={4} md={3} lg={2} key={prod}>
                                                                <Box style={{
                                                                    border: '1px solid #e9ecef',
                                                                    borderRadius: '6px',
                                                                    padding: '0.75rem 1rem',
                                                                    textAlign: 'center'
                                                                }}>
                                                                    <Box style={{ fontSize: '12px', color: '#8898aa', fontWeight: 600, textTransform: 'uppercase', marginBottom: '4px' }}>
                                                                        {getProductString(prod)}
                                                                    </Box>
                                                                    <Box style={{ fontSize: '20px', fontWeight: 700, color: '#32325d' }}>
                                                                        {data.leads}
                                                                    </Box>
                                                                    <Box style={{ fontSize: '11px', color: '#8898aa', marginBottom: '6px' }}>
                                                                        leads
                                                                    </Box>
                                                                    <Box style={{ fontSize: '15px', fontWeight: 600, color: '#2dce89' }}>
                                                                        €{data.revenue.toFixed(2)}
                                                                    </Box>
                                                                    <Box style={{ marginTop: '6px' }}>
                                                                        <Box style={{
                                                                            width: '100%', height: '4px', borderRadius: '2px',
                                                                            backgroundColor: '#e9ecef', overflow: 'hidden'
                                                                        }}>
                                                                            <Box style={{
                                                                                width: `${Math.min(pct, 100)}%`, height: '100%',
                                                                                borderRadius: '2px', backgroundColor: '#5e72e4'
                                                                            }} />
                                                                        </Box>
                                                                        <Box style={{ fontSize: '10px', color: '#8898aa', marginTop: '2px' }}>
                                                                            {pct.toFixed(1)}% of revenue
                                                                        </Box>
                                                                    </Box>
                                                                </Box>
                                                            </Grid>
                                                        )
                                                    })}
                                                </Grid>
                                            </Box>
                                        </Card>
                                    </Grid>
                                )}

                                {/* Balance Deliveries Table */}
                                {showDeliveries.filter(d => d.deliveryType === 'balance').length > 0 && (
                                    <Grid item xs={12} style={{ marginTop: '2rem' }}>
                                        <FSDeliveriesTable
                                            tableType='balance'
                                            deliveries={showDeliveries
                                                .filter(d => d.deliveryType === 'balance')
                                                .map(item => ({
                                                    ...item,
                                                    compositeKey: `${item.companyId}-${item.product}-${item.deliveryType}`
                                                }))
                                                .sort((a, b) => (parseFloat(b.balance) || 0) - (parseFloat(a.balance) || 0))
                                            }
                                            updateDelivery={(delivery) => updateDelivery(delivery)}
                                            showSuccessAlert={(text) => showSuccessAlert(text)}
                                            title='Balance Deliveries'
                                        />
                                    </Grid>
                                )}
                            </Grid>
                        ) : (
                            <Grid container justifyContent='flex-start'>
                                <Grid item xs={12}>
                                    <Box display='flex' justifyContent='center' alignItems='center' style={{ height: '20vh' }}>
                                        <Loader />
                                    </Box>
                                </Grid>
                            </Grid>
                        )}
                    </>
                )}

                {tab === 1 && (
                    <FSBalanceTab showSuccessAlert={(text) => showSuccessAlert(text)} />
                )}
            </Container>
        </>
    )
}

export default FSDeliveries
